import { GraphQLSearchSignedClass, GraphQLSearchVariables } from "@eco/client"
import { DataGridProps, GridSortDirection, GridSortItem, GridSortModel } from "@mui/x-data-grid"
import { useDebounce, useGraphQLSearch } from "hooks"
import { UseEcoQueryOptions } from "hooks/query/useEcoQuery"
import _ from "lodash"
import { createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { GenericDataGrid, GENERIC_DATA_GRID_ID, GenericDataGridProps, GenericDataGridRef } from "./GenericDataGrid"
import { TDataGridSortFilter, useDataGridFilterState } from "./hooks/useDataGridFilterState"

export const GraphQLDataGrid = forwardRef(
    function GraphQLDataGrid ({ 
        GQLRequest,
        onResponseMapping,
        filterDebounce,
        ...props
        }: GraphQLDataGridProps<GraphQLSearchSignedClass>, 
        ref?: React.Ref<GraphQLDataGridRef | undefined>
    ) {
    
    const genericDataGridRef = createRef<GenericDataGridRef>()
    const { filter, setFilter } = useDataGridFilterState(props.id || GENERIC_DATA_GRID_ID, props.isUrlParamsState, props.defaultFilter)
    const getInstance = useCallback(() => new GQLRequest(filter as GraphQLSearchVariables), [filter])
    const [instance, setInstance] = useState(getInstance())
    const debounce = useDebounce()

    useEffect(() => { 
        if (filterDebounce !== 0) {
            debounce(() => {
                updateInstance()
            }, filterDebounce || 250)
        } else {
            updateInstance()
        }
    }, [filter])

    const updateInstance = useCallback(() => {
        const newInstance = getInstance()
        if (!_.isEqual(newInstance.variables, instance.variables)) {
            setInstance(newInstance)
        }
    }, [filter])

    const { data, isLoading, invalidateQuery, resetQuery, removeQuery } = useGraphQLSearch(
        instance, 
        onResponseMapping, 
        props.requestOptions as any
    )

    const handleSortModelChange = useCallback((newModel: GridSortModel) => {
        setFilter((v: TDataGridSortFilter) => ({ ...v, sort: newModel.map((item) => `${item.field} ${item.sort?.toUpperCase() || ''}`) }))
    }, []);

    const sortModel = useMemo((): GridSortModel | undefined => {
        if (typeof filter.sort === 'string') {
            return [{ field: filter.sort, sort: 'asc' }]
        }

        return (filter.sort as Array<string>)?.map<GridSortItem>((item) => {
            const [field, sort] = item.split(' ')
            return { field, sort: sort.toLocaleLowerCase() as GridSortDirection }
        })
    }, [filter])

    const getRef = (): GraphQLDataGridRef => ({
        ...genericDataGridRef.current || {},
        rows: data?.rows || [],
        total: data?.total || 0,
        invalidate: () => invalidateQuery(),
        reset: () => resetQuery(),
        remove: () => removeQuery(),
        instance: getInstance()
    })
    
    useImperativeHandle(ref, getRef)

    return (
        <GenericDataGrid
            {...props}
            rows={data?.rows || []}
            rowCount={data?.total || 0}
            loading={isLoading}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            ref={genericDataGridRef}
            />
    )
})

export type GraphQLDataGridRef = GenericDataGridRef & {
    rows?: DataGridProps['rows']
    total?: DataGridProps['rowCount']
    invalidate: () => void
    reset: () => void
    remove: () => void
    instance: GraphQLSearchSignedClass
}

export type  GraphQLDataGridProps<TClass extends GraphQLSearchSignedClass> = Omit<GenericDataGridProps, 'rows' | 'rowCount' | 'loading'> 
    & {
        /**
         * Class declaration that store all the information for the graphql query
         */
        GQLRequest: { new(variables: GraphQLSearchVariables): TClass }
        /**
         * Mapping function, that transform raw data to a specific data that the GraphQLDataGrid understand
         */
        onResponseMapping: (res: unknown, count?: Record<string, number>) => GraphQLDataGridResponse
        /**
         * The query options
         */
        requestOptions?: UseEcoQueryOptions
        /**
         * Debounce of the filter, passing 0 will disable the debouncing
         */
        filterDebounce?: number
    }

export type GraphQLDataGridResponse = {
    rows: Array<Record<string, any>>
    total: number
}
